import { getColumnDynamicsState, getCustomColDefProperties, type ColumnOrColumnGroup } from '@talos/kyoko';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { PRICE_GRID_MARKET_DATA_COLUMN_DYNAMICS_SPEC } from './constants';
import type { PriceGridMarketDataColumnSettings } from './types';

const PRICE_COLUMN_TITLE = 'Price';
type PriceGridColumnOrGroup = ColumnOrColumnGroup | ColDef | ColGroupDef;
type PriceGridColumnGroupLike = {
  children: PriceGridColumnOrGroup[];
  headerName?: string;
};
type PriceGridMarketDataColumnDynamics = {
  spec?: string;
  state?: PriceGridMarketDataColumnSettings;
  initialState?: PriceGridMarketDataColumnSettings;
};

export function isPriceGridMarketDataColumnGroup(column: PriceGridColumnOrGroup): boolean {
  return getPriceGridMarketDataColumnDynamics(column) != null;
}

export function countPriceGridMarketDataColumns(columns: Array<PriceGridColumnOrGroup> | undefined) {
  return columns?.filter(isPriceGridMarketDataColumnGroup).length ?? 0;
}

export function getNextPriceGridMarketDataColumnTitle(columns: Array<PriceGridColumnOrGroup> | undefined) {
  const titles = new Set(
    columns
      ?.filter(isPriceGridMarketDataColumnGroup)
      .map(getPriceGridMarketDataColumnTitle)
      .filter((title): title is string => typeof title === 'string' && title.length > 0)
  );

  if (!titles.has(PRICE_COLUMN_TITLE)) {
    return PRICE_COLUMN_TITLE;
  }

  let index = 2;
  while (titles.has(`${PRICE_COLUMN_TITLE}(${index})`)) {
    index += 1;
  }

  return `${PRICE_COLUMN_TITLE}(${index})`;
}

function getPriceGridMarketDataColumnTitle(column: PriceGridColumnOrGroup) {
  if (isPriceGridColumnGroupLike(column)) {
    const settings = getColumnDynamicsState(getPriceGridMarketDataColumnDynamics(column));
    return settings?.title ?? column.headerName;
  }

  return undefined;
}

function getPriceGridMarketDataColumnDynamics(
  column: PriceGridColumnOrGroup
): PriceGridMarketDataColumnDynamics | undefined {
  if (isPriceGridColumnGroupLike(column)) {
    return column.children.map(getPriceGridMarketDataColumnDynamics).find(dynamics => dynamics != null);
  }

  const sourceDynamics = (column as ColumnOrColumnGroup).dynamics as PriceGridMarketDataColumnDynamics | undefined;
  if (isPriceGridMarketDataColumnDynamics(sourceDynamics)) {
    return sourceDynamics;
  }

  const dynamics = getCustomColDefProperties(column as ColDef)?.dynamics as
    | PriceGridMarketDataColumnDynamics
    | undefined;
  return isPriceGridMarketDataColumnDynamics(dynamics) ? dynamics : undefined;
}

function isPriceGridColumnGroupLike(column: PriceGridColumnOrGroup): column is PriceGridColumnGroupLike {
  return Array.isArray((column as { children?: unknown }).children);
}

function isPriceGridMarketDataColumnDynamics(
  dynamics: PriceGridMarketDataColumnDynamics | undefined
): dynamics is PriceGridMarketDataColumnDynamics {
  return dynamics?.spec === PRICE_GRID_MARKET_DATA_COLUMN_DYNAMICS_SPEC;
}
